import type { Category, PaginationMeta } from '../types/models.js'
import { API_BASE } from './client.js'
import { ApiError } from '../types/api.js'

function token(): string | null {
  return localStorage.getItem('mf_access')
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = token()
  if (t) headers['Authorization'] = `Bearer ${t}`
  return headers
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    // non-JSON error response
  }
  const errBody =
    (body as { error?: { code: string; message: string; details?: Record<string, unknown> } } | null)
      ?.error ?? { code: 'UNKNOWN', message: fallback }
  return new ApiError(res.status, errBody)
}

export interface CategoryListResponse {
  data: Category[]
  meta: PaginationMeta
}

export interface CreateCategoryData {
  name: string
  slug?: string
  description?: string
  sortOrder?: number
}

export interface UpdateCategoryData {
  name?: string
  description?: string
  sortOrder?: number
}

export type CategoryLifecycleAction = 'suspend' | 'activate' | 'archive'

function categoriesPath(organizationId: string, menuId: string): string {
  return `${API_BASE}/organizations/${organizationId}/menus/${menuId}/categories`
}

export interface GetCategoriesParams {
  /** Only categories that can currently receive a Product (not ARCHIVED chain). */
  writable?: boolean
}

export async function getCategories(
  organizationId: string,
  menuId: string,
  params: GetCategoriesParams = {},
): Promise<CategoryListResponse> {
  const query = new URLSearchParams()
  if (params.writable) query.set('writable', 'true')
  const qs = query.toString()
  const res = await fetch(`${categoriesPath(organizationId, menuId)}${qs ? `?${qs}` : ''}`, { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load categories')
  return res.json() as Promise<CategoryListResponse>
}

export async function getCategory(
  organizationId: string,
  menuId: string,
  categoryId: string,
): Promise<Category> {
  const res = await fetch(`${categoriesPath(organizationId, menuId)}/${categoryId}`, { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load category')
  const b = await res.json()
  return b.data
}

export async function createCategory(
  organizationId: string,
  menuId: string,
  data: CreateCategoryData,
): Promise<Category> {
  const res = await fetch(categoriesPath(organizationId, menuId), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to create category')
  const b = await res.json()
  return b.data
}

export async function updateCategory(
  organizationId: string,
  menuId: string,
  categoryId: string,
  data: UpdateCategoryData,
): Promise<Category> {
  const res = await fetch(`${categoriesPath(organizationId, menuId)}/${categoryId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to update category')
  const b = await res.json()
  return b.data
}

async function lifecycleCategory(
  organizationId: string,
  menuId: string,
  categoryId: string,
  action: CategoryLifecycleAction,
): Promise<Category> {
  const res = await fetch(`${categoriesPath(organizationId, menuId)}/${categoryId}/${action}`, {
    method: 'POST',
    headers: authHeaders(),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to change category status')
  const b = await res.json()
  return b.data
}

export function suspendCategory(
  organizationId: string,
  menuId: string,
  categoryId: string,
): Promise<Category> {
  return lifecycleCategory(organizationId, menuId, categoryId, 'suspend')
}

export function activateCategory(
  organizationId: string,
  menuId: string,
  categoryId: string,
): Promise<Category> {
  return lifecycleCategory(organizationId, menuId, categoryId, 'activate')
}

export function archiveCategory(
  organizationId: string,
  menuId: string,
  categoryId: string,
): Promise<Category> {
  return lifecycleCategory(organizationId, menuId, categoryId, 'archive')
}